/* -----------------Globals--------------- */
import { useCallback, useEffect, useMemo, useState } from "react";

/* -----------------UI--------------- */

/* -----------------Helpers & Hooks--------------- */
import { isIOS, isWeb } from "@helpers/platform";
import { getUserPrompt } from "../components/QASearch/helpers";
import { useAilaStore } from "../hooks/useAilaStore";
import useSessionStore from "../hooks/useSessionStore";

import { useCustomNavigation } from "@hooks/useCustomNavigation";
import {
	useFocusEffect,
	useLocalSearchParams,
	usePathname,
	useRouter,
} from "expo-router";
import { Chip, FAB, Portal } from "react-native-paper";
import QAChipsWrapper from "../components/QASearch/QAChipsWrapper";

import { useAppStore } from "@hooks/useAppStore";
/* -----------------Child components--------------- */
import PostAnswerModal from "../components/postanswer/PostAnswerModal";

/* -----------------Types--------------- */
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
	AI_SUBMITTABLE_VARIANTS,
	MAPPED_ITEM_TYPES,
	sessionMaxWidth,
} from "../constants";
import { getParentStem, replaceBrackets } from "../hooks/useAIFeedback";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";

import { useExamContext } from "@features/exam/contexts/ExamContext";
import { FeedbackDialog } from "@features/feedback/components/dialogs/FeedbackDialog";
import { defaultFeedbackSubtitle } from "@features/feedback/constants";
import { useCopyToClipboard } from "@hooks/useCopyToCllipboard";
import { useDevice } from "@hooks/useDevice";
import type { OriginalQuestionData } from "@memorang/features/src/qa-search/types/question";
import Box from "@memorang/ui/src/components/Box";
import Button from "@memorang/ui/src/components/Button";
import ProfileIcon from "@memorang/ui/src/components/ChatUI/ProfileIcon";
import { useTranslation } from "react-i18next";
import { calculateXp } from "../components/postanswer/helper";
import { getTotalScoreForAiItem } from "../helpers/ai-feedback";
import { getIntlExplanationForChoice } from "../helpers/session";
import useCurrentSession from "../hooks/useCurrentSession";
import { useStimulusStore } from "../hooks/useStimulusStore";

const StudySessionFeedbackContainer = ({
	hasParentStem,
}: {
	hasParentStem?: boolean;
}) => {
	const { t } = useTranslation();
	const pathname = usePathname();

	const { index } = useLocalSearchParams<{
		index: string;
		prev: string;
	}>();

	const [showSuggestCorrectionDialog, setShowSuggestCorrectionDialog] =
		useState(false);

	const { isMobile } = useDevice();
	const showStimulusModal = useStimulusStore(
		(state) => state.showStimulusModal,
	);

	const { handleSuggestCorrectionNavigation } = useCustomNavigation();

	const toggleShowSuggestCorrectionDialog = () => {
		setShowSuggestCorrectionDialog((prev) => !prev);
	};
	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const answers = useSessionStore((state) => state.answers);
	const userResponse = useAIFeedbackStore((state) => state.userResponse);

	const isAiFeedbackStreaming = useAIFeedbackStore((state) => state.streaming);
	const { copyToClipboard } = useCopyToClipboard();
	const { currentItem } = useCurrentSession();
	const aiFeedback = useAIFeedbackStore(
		(state) => state.aiResponse?.[currentItem.id!]?.explanation,
	);
	const aiTranscription = useAIFeedbackStore(
		(state) => state.aiResponse?.[currentItem.id!]?.transcription,
	);
	const sessionView = useAppStore((state) => state.sessionView);

	const attempts = useSessionStore((state) => state.attempts);

	const currentAttempt = attempts.get(`${currentItem.id!}`);

	const attemptKeys = Object.keys(currentAttempt?.choiceAttempts || {});

	const numAttempts = attemptKeys.length;

	const showXpEarned = sessionView?.postAnswerStats?.showXpEarned;
	const showNumAttempts = sessionView?.postAnswerStats?.showNumAttempts;

	const { content, metadata, stimulus } = currentItem;
	const choices = content?.choices ?? [];
	const stem = content?.stem!;
	const type = metadata?.type!;
	const itemType = MAPPED_ITEM_TYPES[type];
	const stimulusType = stimulus?.metadata?.type;

	const isAiItemType = AI_SUBMITTABLE_VARIANTS.includes(itemType);
	const isStreaming = useAIFeedbackStore((store) => store.streaming);
	const topics = currentItem.tags?.map((tag) => tag.label ?? "") || [];
	const itemId = currentItem.id!;

	const currentAnswer = answers?.get(currentItem.id!) || currentItem.answer;

	const currentUserResponse = userResponse?.[itemId] as string | undefined;

	const combinedCitations = useMemo(() => {
		return metadata?.citations?.slice(0, 2) ?? [];
	}, [metadata]);
	const numberOfWords =
		type === "ACADEMIC_DISCUSSION_RESPONSE" ||
		type === "INTEGRATED_WRITING_RESPONSE" ||
		type === "WRITING_EMAIL"
			? currentUserResponse?.trim().split(/\s+/).length || 0
			: undefined;

	const { bottom } = useSafeAreaInsets();
	const isAssistantVisible = useAilaStore((state) => state.showDrawer);
	const updateIsAssistantVisible = useAilaStore(
		(state) => state.updateShowDrawer,
	);
	const updateSelectedQuery = useAilaStore(
		(state) => state.updateSelectedQuery,
	);

	const aiAssistantAlias = useAppStore((store) => store.app.aiAssistant.alias);
	const aiAssistantIcon = useAppStore((store) => store.app.aiAssistant.icon);

	const lastAttemptChoiceId = attemptKeys[attemptKeys.length - 1];

	const numResponses = currentItem?.content?.choices?.find(
		(choice) => choice.id === lastAttemptChoiceId,
	)?.numFirstSelections;

	const router = useRouter();

	const handleAssistantFabPress = () => {
		updateIsAssistantVisible(true);
		if (!isWeb) {
			router.push(`/aila/${currentItem.id}`);
		}
	};

	useFocusEffect(
		useCallback(() => {
			if (!isWeb) {
				updateIsAssistantVisible(false);
				updateSelectedQuery("");
			}
		}, [updateIsAssistantVisible, updateSelectedQuery]),
	);

	useEffect(() => {
		if (isSummativeTest && inReviewMode) {
			updateIsAssistantVisible(true);
		}
	}, [isSummativeTest, inReviewMode, updateIsAssistantVisible]);

	const {
		currentExamContext: { examName },
	} = useExamContext();

	const renderSuggestCorrectionButton = () => {
		if (!isSummativeTest) {
			return (
				<Box alignSelf="center" marginTop={16} paddingBottom={100}>
					<Button
						onPress={() =>
							handleSuggestCorrectionNavigation(
								toggleShowSuggestCorrectionDialog,
								`${currentItem.id!}`,
							)
						}
						icon={"flag-outline"}
					>
						{t("session.suggestCorrection")}
					</Button>
					{showSuggestCorrectionDialog && (
						<FeedbackDialog
							visible={showSuggestCorrectionDialog}
							view={pathname}
							feedbackSubtitle={t(defaultFeedbackSubtitle)}
							itemId={`${currentItem.id!}`}
							handleDismiss={toggleShowSuggestCorrectionDialog}
						/>
					)}
				</Box>
			);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const renderCommonPostAnswerModal = useMemo(() => {
		const shouldRenderPostAnswerModal = isSummativeTest
			? inReviewMode
			: currentAnswer != null;

		const shouldRenderFollowUps =
			!!shouldRenderPostAnswerModal && isWeb && !(isAiItemType && isStreaming);

		const confidence = currentAnswer?.confidence;
		const timeTaken = currentAnswer?.timeTaken;
		const correct = currentAnswer?.correct;
		const maximumScore =
			(currentItem.response?.maximumScore ||
				currentItem.response?.itemPoints) ??
			getTotalScoreForAiItem(itemType);

		const correctChoices = choices?.filter(({ isCorrect }) => isCorrect);

		const selectedChoice = currentAnswer?.selectedChoiceIds?.map((choiceId) => {
			const choice = choices.find((choice) => choice.id === choiceId);
			return {
				isCorrect: choice?.isCorrect,
				stem: choice?.stem,
				text: choice?.text,
			};
		});
		const data = {
			stem,
			explanation: isAiItemType
				? (aiFeedback ?? "")
				: (getIntlExplanationForChoice(correctChoices[0]) ?? ""),
			choices,
			topics,
			userResponse: isAiItemType
				? (aiTranscription ?? currentUserResponse)
				: undefined,
		};
		const userPrompt = replaceBrackets(
			`${getParentStem(currentItem)}\n\n${getUserPrompt(data, examName)}`,
		);
		const originalQuestionData: OriginalQuestionData = {
			stem: data.stem,
			explanation: data?.explanation ?? "",
			topics,
			choices:
				data.choices.map((choice) => ({
					text: choice.stem,
					isCorrect: choice.isCorrect,
				})) ?? [],
			selectedChoice: selectedChoice ?? [],
		};

		const AiFab = () => {
			// Render above the bottom app bar + some padding
			const bottomPosition = 80 + 24;

			return (
				<Portal>
					<FAB
						icon={(_props) => <ProfileIcon uri={aiAssistantIcon} />}
						label={aiAssistantAlias}
						style={{
							position: "absolute",
							// account for safe area inset on iOS
							bottom: bottomPosition + (isIOS ? bottom - 12 : 0),
							right: 24,
						}}
						onPress={handleAssistantFabPress}
					/>
				</Portal>
			);
		};

		const isAiLAFabVisible =
			!isAssistantVisible &&
			shouldRenderPostAnswerModal &&
			!isAiFeedbackStreaming &&
			!showStimulusModal;
		const numTotalAttempts = currentItem.content?.choices?.length || 0;

		const attemptsStatValue = `${numAttempts} of ${numTotalAttempts}`;
		const xpEarned = calculateXp(numAttempts, false);
		return (
			<>
				{isAiLAFabVisible && <AiFab />}
				{shouldRenderPostAnswerModal && (
					<PostAnswerModal
						itemCriteria={currentItem.response?.criteria}
						choices={choices}
						timeTaken={timeTaken ?? 0}
						confidence={confidence}
						showExplanation
						correct={correct}
						feedback={currentItem.content?.feedback}
						tags={currentItem.tags}
						showXpEarned={showXpEarned}
						currentQuestionAnswered
						currentItemIndex={currentItemIndex}
						itemId={currentItem.id!}
						attemptsStatValue={attemptsStatValue}
						userPrompt={userPrompt}
						numResponses={numResponses}
						showNumAttempts={showNumAttempts}
						xpEarned={xpEarned}
						isSummativeTest={isSummativeTest}
						renderSuggestCorrectionButton={renderSuggestCorrectionButton}
						originalQuestionData={originalQuestionData}
						itemType={itemType}
						numberOfWords={numberOfWords}
						maximumScore={maximumScore}
						originalItemType={type}
						currentItemAnswer={currentItem.answer}
						stimulusType={stimulusType}
						citations={combinedCitations}
					/>
				)}
				<Box margin={16} gap={16}>
					{(!isSummativeTest || inReviewMode) && (
						<>
							<QAChipsWrapper
								itemId={currentItem.id!}
								userPrompt={userPrompt}
								shouldRender={shouldRenderFollowUps}
								originalQuestionData={originalQuestionData}
								loading={isAiItemType && isStreaming}
							/>
							{shouldRenderFollowUps && (
								<Chip
									compact
									onPress={() =>
										copyToClipboard(`${itemId}`, "Item ID copied to clipboard")
									}
									style={{
										alignSelf: "baseline",
									}}
								>
									Item ID: #{itemId}
								</Chip>
							)}
						</>
					)}
				</Box>
			</>
		);
	}, [
		answers,
		currentItem,
		currentItemIndex,
		examName,
		inReviewMode,
		isSummativeTest,
		isAssistantVisible,
		isAiFeedbackStreaming,
		showStimulusModal,
	]);

	return (
		<Box
			maxWidth={
				isSummativeTest ? sessionMaxWidth.summative : sessionMaxWidth.formative
			}
			alignSelf={
				hasParentStem ? undefined : isSummativeTest ? "flex-start" : "center"
			}
			paddingHorizontal={isMobile ? 16 : 0}
		>
			{renderCommonPostAnswerModal}
			{isWeb && currentAnswer != null && renderSuggestCorrectionButton()}
		</Box>
	);
};

export default StudySessionFeedbackContainer;
